import { useState } from 'react';
import moment from 'moment/moment';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { METRICS, OVERALL_DESCRIPTION, bandClass, formatNumber } from '../helpers';
import { cn } from '@/helpers/shadcn';

const GROUP_BORDER = 'border-l border-border';
const STICKY_EDGE = 'shadow-[inset_-1px_0_0_0_hsl(var(--border))]';
// The company column has a fixed width so the score column can stick beside it
const COMPANY_WIDTH = 'w-56 min-w-56 max-w-56';
const SCORE_LEFT = 'left-56';
const HOVER_CARD_WIDTH = 256;

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  }
  return sortDir === 'asc' ? <ArrowUp className="ml-1 inline h-3 w-3" /> : <ArrowDown className="ml-1 inline h-3 w-3" />;
};

const HoverCard = ({ hover }) => {
  const { row, column, rect } = hover;
  const cell = row.cells[column.key];
  const fitsRight = rect.right + 8 + HOVER_CARD_WIDTH < window.innerWidth;
  const left = fitsRight ? rect.right + 8 : rect.left - 8 - HOVER_CARD_WIDTH;
  const top = Math.min(rect.top, window.innerHeight - 220);

  return (
    <div className="pointer-events-none fixed z-50 rounded-md border bg-popover p-3 text-popover-foreground shadow-md" style={{ left, top, width: HOVER_CARD_WIDTH }}>
      <p className="text-sm font-medium">{row.name}</p>
      <p className="mb-2 text-xs text-muted-foreground">
        {column.features.length > 1 ? `${column.label} (all ${column.features.length} features)` : `${column.areaLabel} · ${column.label}`}
      </p>
      {METRICS.map((metric) => (
        <div key={metric.key} className={cn('flex justify-between gap-4 text-xs', metric.key === 'overall' && 'mb-1 border-b pb-1')}>
          <span className="text-muted-foreground">{metric.label}</span>
          <span className="font-medium tabular-nums">{cell[metric.key] === null ? 'Not tracked' : cell[metric.key].toLocaleString()}</span>
        </div>
      ))}
      <div className="flex justify-between gap-4 text-xs">
        <span className="text-muted-foreground">Last created</span>
        <span className="font-medium">{cell.last_created ? moment(cell.last_created).format('D MMM YYYY') : '—'}</span>
      </div>
    </div>
  );
};

const MatrixCell = ({ value, intensity, onHover, onLeave, className }) => {
  if (value === null) {
    return (
      <td className={cn('h-8 border-b bg-muted/40 text-center text-xs text-muted-foreground/60', className)} onMouseEnter={onHover} onMouseLeave={onLeave}>
        —
      </td>
    );
  }
  if (!value) {
    return <td className={cn('h-8 border-b', className)} onMouseEnter={onHover} onMouseLeave={onLeave} />;
  }
  return (
    <td className={cn('h-8 border-b p-0.5', className)} onMouseEnter={onHover} onMouseLeave={onLeave}>
      <div className={cn('flex h-full items-center justify-center rounded-sm text-xs font-medium tabular-nums', bandClass(intensity))}>{formatNumber(value)}</div>
    </td>
  );
};

const UsageMatrix = ({ rows, columns, groups, collapsed, metric, intensities, scoreIntensities, sortField, sortDir, onSort, onSelectCompany }) => {
  const [hover, setHover] = useState(null);

  const groupStarts = new Set(groups.reduce((starts, group, i) => [...starts, i === 0 ? 0 : starts[starts.length - 1] + groups[i - 1].span], []));

  return (
    <div className="relative max-h-[70vh] overflow-auto rounded-md border">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          {/* Collapsed, each column is an area already, so the grouped row would repeat it */}
          {!collapsed && (
            <tr>
              <th colSpan={2} className={cn('sticky left-0 top-0 z-30 h-7 bg-muted', STICKY_EDGE)} />
              {groups.map((group) => (
                <th
                  key={group.area}
                  colSpan={group.span}
                  className={cn(
                    'sticky top-0 z-20 h-7 whitespace-nowrap bg-muted px-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground',
                    GROUP_BORDER
                  )}
                >
                  {group.label}
                </th>
              ))}
            </tr>
          )}
          <tr>
            <th
              className={cn(
                'sticky left-0 z-30 cursor-pointer select-none whitespace-nowrap border-b bg-background px-3 py-2 text-left align-bottom font-medium text-muted-foreground',
                COMPANY_WIDTH,
                collapsed ? 'top-0' : 'top-7'
              )}
              onClick={() => onSort('company')}
            >
              Company <SortIcon field="company" sortField={sortField} sortDir={sortDir} />
            </th>
            <th
              title={OVERALL_DESCRIPTION}
              className={cn(
                'sticky z-30 w-20 cursor-pointer select-none whitespace-nowrap border-b bg-background px-2 py-2 text-center align-bottom text-xs font-medium text-muted-foreground',
                SCORE_LEFT,
                collapsed ? 'top-0' : 'top-7',
                STICKY_EDGE
              )}
              onClick={() => onSort('score')}
            >
              Overall
              <SortIcon field="score" sortField={sortField} sortDir={sortDir} />
            </th>
            {columns.map((column, i) => (
              <th
                key={column.key}
                className={cn(
                  'sticky z-20 min-w-[4.5rem] max-w-[6rem] cursor-pointer select-none border-b bg-background px-1.5 py-2 text-center align-bottom text-[11px] font-medium leading-tight text-muted-foreground',
                  collapsed ? 'top-0' : 'top-7',
                  groupStarts.has(i) && GROUP_BORDER
                )}
                onClick={() => onSort(column.key)}
              >
                {column.label}
                <SortIcon field={column.key} sortField={sortField} sortDir={sortDir} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.uid} className="group cursor-pointer" onClick={() => onSelectCompany(row.uid)}>
              <td className={cn('sticky left-0 z-10 truncate border-b bg-background px-3 font-medium group-hover:bg-muted', COMPANY_WIDTH)} title={row.name}>
                {row.name}
              </td>
              <td className={cn('sticky z-10 h-8 border-b bg-background p-0.5 group-hover:bg-muted', SCORE_LEFT, STICKY_EDGE)}>
                {row.score > 0 && (
                  <div className={cn('flex h-full items-center justify-center rounded-sm text-xs font-semibold tabular-nums', bandClass(scoreIntensities[row.uid]))}>
                    {row.score}
                  </div>
                )}
              </td>
              {columns.map((column, i) => (
                <MatrixCell
                  key={column.key}
                  value={row.cells[column.key][metric]}
                  intensity={intensities[column.key][row.uid]}
                  onHover={(e) => setHover({ row, column, rect: e.currentTarget.getBoundingClientRect() })}
                  onLeave={() => setHover(null)}
                  className={groupStarts.has(i) ? GROUP_BORDER : undefined}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hover && <HoverCard hover={hover} />}
    </div>
  );
};

export default UsageMatrix;
